// src/pages/AdminVerifyEntrance.js

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Swal from "sweetalert2";
import {
    FaArrowLeft,
    FaArrowRight,
    FaCheckCircle,
    FaClock,
    FaEye,
    FaFileInvoiceDollar,
    FaFilter,
    FaMoneyBillWave,
    FaSearch,
    FaShieldAlt,
    FaSpinner,
    FaTimesCircle,
    FaUserCheck,
    FaUsers,
} from "react-icons/fa";
import api from "../api/api";

const PAGE_SIZE = 8;

// IMPORTANT:
// The public payment endpoint is:
// /entrance-applications/payment/
//
// The ADMIN payment listing endpoint is:
// /entrance-applications/payments/
const PAYMENT_LIST_ENDPOINT = "/api/entrance-applications/payments/";

const VERIFY_ENDPOINT = (applicationNumber) =>
    `/api/entrance-applications/payments/${encodeURIComponent(
        applicationNumber
    )}/verify/`;

const COLORS = {
    navy: "#062A4B",
    blue: "#063A6B",
    gold: "#F6C944",
    orange: "#C0750D",
    green: "#4FAE22",
    light: "#F5F8FC",
    white: "#FFFFFF",
    text: "#172033",
    muted: "#687386",
    border: "#DCE4EE",
    danger: "#C62828",
    warning: "#B7791F",
    success: "#2E7D32",
};

const swalBaseConfig = {
    customClass: {
        popup: "passion-aids-swal-popup",
        title: "passion-aids-swal-title",
        htmlContainer: "passion-aids-swal-content",
        confirmButton: "passion-aids-swal-confirm",
        cancelButton: "passion-aids-swal-cancel",
    },
    buttonsStyling: false,
    allowOutsideClick: false,
};

const showSuccess = (title, text) =>
    Swal.fire({
        ...swalBaseConfig,
        icon: "success",
        title,
        text,
        confirmButtonText: "Continue",
    });

const showError = (title, text) =>
    Swal.fire({
        ...swalBaseConfig,
        icon: "error",
        title,
        text,
        confirmButtonText: "Close",
    });

const formatDateTime = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatAmount = (amount, currency = "USD") => {
    if (amount === null || amount === undefined || amount === "") {
        return "—";
    }

    const numericAmount = Number(amount);

    if (Number.isNaN(numericAmount)) {
        return `${currency || ""} ${amount}`.trim();
    }

    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currency || "USD",
            minimumFractionDigits: 2,
        }).format(numericAmount);
    } catch {
        return `${currency || "USD"} ${numericAmount.toFixed(2)}`;
    }
};

const normalizeResults = (responseData) => {
    if (!responseData) {
        return {
            results: [],
            count: 0,
            next: null,
            previous: null,
        };
    }

    if (Array.isArray(responseData)) {
        return {
            results: responseData,
            count: responseData.length,
            next: null,
            previous: null,
        };
    }

    if (Array.isArray(responseData.results)) {
        return {
            results: responseData.results,
            count:
                typeof responseData.count === "number"
                    ? responseData.count
                    : responseData.results.length,
            next: responseData.next || null,
            previous: responseData.previous || null,
        };
    }

    if (Array.isArray(responseData.data)) {
        return {
            results: responseData.data,
            count:
                typeof responseData.count === "number"
                    ? responseData.count
                    : responseData.data.length,
            next: responseData.next || null,
            previous: responseData.previous || null,
        };
    }

    if (Array.isArray(responseData.payments)) {
        return {
            results: responseData.payments,
            count:
                typeof responseData.count === "number"
                    ? responseData.count
                    : responseData.payments.length,
            next: responseData.next || null,
            previous: responseData.previous || null,
        };
    }

    return {
        results: [],
        count: 0,
        next: null,
        previous: null,
    };
};

const getStatusLabel = (status) => {
    const normalized = String(status || "")
        .trim()
        .toLowerCase();

    if (normalized === "verified") return "Verified";
    if (normalized === "pending") return "Pending";
    if (normalized === "submitted") return "Submitted";
    if (normalized === "rejected") return "Rejected";
    if (normalized === "failed") return "Failed";

    return status || "Unknown";
};

const StatusBadge = ({ status }) => {
    const normalized = String(status || "")
        .trim()
        .toLowerCase();

    let background = "#EEF2F7";
    let color = COLORS.muted;
    let Icon = FaClock;

    if (normalized === "verified") {
        background = "#EAF7EC";
        color = COLORS.success;
        Icon = FaCheckCircle;
    } else if (
        normalized === "pending" ||
        normalized === "submitted"
    ) {
        background = "#FFF7E3";
        color = COLORS.warning;
        Icon = FaClock;
    } else if (
        normalized === "rejected" ||
        normalized === "failed"
    ) {
        background = "#FDECEC";
        color = COLORS.danger;
        Icon = FaTimesCircle;
    }

    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                background,
                color,
                fontSize: 12,
                fontWeight: 800,
                whiteSpace: "nowrap",
            }}
        >
            <Icon size={12} />
            {getStatusLabel(status)}
        </span>
    );
};

const AdminVerifyEntrance = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [verifyingId, setVerifyingId] = useState(null);

    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    const [statusFilter, setStatusFilter] = useState("");

    const [page, setPage] = useState(1);
    const [count, setCount] = useState(0);

    const [nextUrl, setNextUrl] = useState(null);
    const [previousUrl, setPreviousUrl] = useState(null);

    const [selectedPayment, setSelectedPayment] = useState(null);

    const requestIdRef = useRef(0);

    const totalPages = useMemo(() => {
        if (!count) return 1;
        return Math.max(1, Math.ceil(count / PAGE_SIZE));
    }, [count]);

    const getErrorMessage = useCallback((error) => {
        if (!error) {
            return "An unexpected error occurred.";
        }

        const responseData = error?.response?.data;

        if (typeof responseData === "string") {
            return responseData;
        }

        if (responseData?.detail) {
            return responseData.detail;
        }

        if (responseData?.message) {
            return responseData.message;
        }

        if (responseData?.error) {
            return responseData.error;
        }

        if (responseData && typeof responseData === "object") {
            const messages = [];

            Object.entries(responseData).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    messages.push(`${key}: ${value.join(", ")}`);
                } else if (typeof value === "string") {
                    messages.push(`${key}: ${value}`);
                }
            });

            if (messages.length) {
                return messages.join(" ");
            }
        }

        if (error?.message) {
            return error.message;
        }

        return "Unable to complete the request.";
    }, []);

    const loadPayments = useCallback(
        async (requestedPage = 1) => {
            const requestId = ++requestIdRef.current;

            setLoading(true);

            try {
                const response = await api.get(PAYMENT_LIST_ENDPOINT, {
                    params: {
                        page: requestedPage,
                        page_size: PAGE_SIZE,
                        search: search.trim(),
                        status: statusFilter,
                    },
                });

                if (requestId !== requestIdRef.current) {
                    return;
                }

                const normalized = normalizeResults(response.data);

                setPayments(normalized.results);
                setCount(normalized.count);
                setNextUrl(normalized.next);
                setPreviousUrl(normalized.previous);
                setPage(requestedPage);

                if (
                    normalized.results.length === 0 &&
                    requestedPage > 1 &&
                    requestedPage > Math.ceil(normalized.count / PAGE_SIZE)
                ) {
                    setPage(Math.max(1, Math.ceil(normalized.count / PAGE_SIZE)));
                }
            } catch (error) {
                if (requestId !== requestIdRef.current) {
                    return;
                }

                const statusCode = error?.response?.status;

                if (statusCode === 401) {
                    showError(
                        "Authentication Required",
                        "Your session may have expired. Please sign in again."
                    );
                } else if (statusCode === 403) {
                    showError(
                        "Access Denied",
                        "Only authorized administrators can access entrance payment verification."
                    );
                } else if (statusCode === 404) {
                    showError(
                        "Endpoint Not Found",
                        "The administrator payment endpoint was not found. Please confirm that /entrance-applications/payments/ is registered in the Django URLs."
                    );
                } else if (statusCode === 405) {
                    showError(
                        "Invalid API Method",
                        "The server is receiving this request on the wrong payment endpoint. The admin payment list must use /entrance-applications/payments/."
                    );
                } else {
                    showError(
                        "Unable to Load Payments",
                        getErrorMessage(error)
                    );
                }

                setPayments([]);
                setCount(0);
                setNextUrl(null);
                setPreviousUrl(null);
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        },
        [getErrorMessage, search, statusFilter]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            const normalizedSearch = searchInput.trim();

            setSearch((previous) => {
                if (previous === normalizedSearch) {
                    return previous;
                }

                return normalizedSearch;
            });
        }, 450);

        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        loadPayments(1);
    }, [search, statusFilter, loadPayments]);

    const handleRefresh = () => {
        loadPayments(page);
    };

    const handleSearchChange = (event) => {
        setSearchInput(event.target.value);
    };

    const handleStatusChange = (event) => {
        setStatusFilter(event.target.value);
    };

    const clearFilters = () => {
        setSearchInput("");
        setSearch("");
        setStatusFilter("");
        setPage(1);
    };

    const openPaymentDetails = (payment) => {
        setSelectedPayment(payment);
    };

    const closePaymentDetails = () => {
        if (!verifyingId) {
            setSelectedPayment(null);
        }
    };

    const verifyPayment = async (payment) => {
        if (!payment?.application_number) {
            showError(
                "Missing Application Number",
                "This payment record does not contain a valid application number."
            );
            return;
        }

        const normalizedStatus = String(payment.status || "")
            .trim()
            .toLowerCase();

        if (normalizedStatus === "verified") {
            showSuccess(
                "Already Verified",
                "This entrance payment has already been verified."
            );
            return;
        }

        if (
            normalizedStatus !== "pending" &&
            normalizedStatus !== "submitted"
        ) {
            showError(
                "Cannot Verify Payment",
                "Only pending or submitted payments can be verified."
            );
            return;
        }

        const confirmation = await Swal.fire({
            ...swalBaseConfig,
            icon: "question",
            title: "Verify Entrance Payment?",
            html: `
                <div style="text-align:left;line-height:1.7;">
                    <div style="margin-bottom:8px;">
                        <strong>Applicant:</strong>
                        ${payment.applicant_name || "—"}
                    </div>
                    <div style="margin-bottom:8px;">
                        <strong>Application:</strong>
                        ${payment.application_number || "—"}
                    </div>
                    <div style="margin-bottom:8px;">
                        <strong>Amount:</strong>
                        ${formatAmount(
                            payment.amount,
                            payment.currency
                        )}
                    </div>
                    <div>
                        <strong>Reference:</strong>
                        ${payment.transaction_reference || "—"}
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Yes, Verify Payment",
            cancelButtonText: "Cancel",
        });

        if (!confirmation.isConfirmed) {
            return;
        }

        const paymentId =
            payment.id || payment.application_number;

        setVerifyingId(paymentId);

        try {
            const response = await api.post(
                VERIFY_ENDPOINT(payment.application_number)
            );

            const message =
                response?.data?.message ||
                "The entrance payment has been successfully verified.";

            await showSuccess("Payment Verified", message);

            await loadPayments(page);

            setSelectedPayment((current) => {
                if (
                    !current ||
                    current.application_number !==
                        payment.application_number
                ) {
                    return current;
                }

                return {
                    ...current,
                    status:
                        response?.data?.payment?.status ||
                        "verified",
                    status_display:
                        response?.data?.payment?.status_display ||
                        "Verified",
                    verified_at:
                        response?.data?.payment?.verified_at ||
                        new Date().toISOString(),
                    can_verify: false,
                };
            });
        } catch (error) {
            const statusCode = error?.response?.status;

            if (statusCode === 403) {
                showError(
                    "Verification Denied",
                    "You do not have permission to verify entrance payments."
                );
            } else if (statusCode === 404) {
                showError(
                    "Payment Not Found",
                    "The entrance application or payment record could not be found."
                );
            } else if (statusCode === 405) {
                showError(
                    "Invalid Verification Endpoint",
                    "The verification request must be sent to /entrance-applications/payments/<application_number>/verify/."
                );
            } else {
                showError(
                    "Verification Failed",
                    getErrorMessage(error)
                );
            }
        } finally {
            setVerifyingId(null);
        }
    };

    const pageStart = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;

    const pageEnd =
        count === 0
            ? 0
            : Math.min(page * PAGE_SIZE, count);

    return (
        <div
            style={{
                minHeight: "100vh",
                background: COLORS.light,
                padding: "24px",
                color: COLORS.text,
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
            }}
        >
            <style>
                {`
                    .passion-aids-swal-popup {
                        border-radius: 18px !important;
                        padding: 26px !important;
                        border: 1px solid #DCE4EE !important;
                        box-shadow: 0 24px 70px rgba(6, 42, 75, 0.18) !important;
                    }

                    .passion-aids-swal-title {
                        color: #062A4B !important;
                        font-size: 22px !important;
                        font-weight: 800 !important;
                    }

                    .passion-aids-swal-content {
                        color: #687386 !important;
                        font-size: 14px !important;
                    }

                    .passion-aids-swal-confirm {
                        border: none !important;
                        border-radius: 10px !important;
                        background: #062A4B !important;
                        color: #FFFFFF !important;
                        padding: 11px 18px !important;
                        font-weight: 700 !important;
                        cursor: pointer !important;
                        margin: 0 5px !important;
                    }

                    .passion-aids-swal-cancel {
                        border: 1px solid #DCE4EE !important;
                        border-radius: 10px !important;
                        background: #F5F8FC !important;
                        color: #172033 !important;
                        padding: 11px 18px !important;
                        font-weight: 700 !important;
                        cursor: pointer !important;
                        margin: 0 5px !important;
                    }

                    .entrance-admin-scroll {
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                    }

                    .entrance-admin-table {
                        width: 100%;
                        min-width: 1180px;
                        border-collapse: collapse;
                    }

                    .entrance-admin-table th {
                        background: #F7F9FC;
                        color: #687386;
                        font-size: 11px;
                        font-weight: 800;
                        letter-spacing: .04em;
                        text-transform: uppercase;
                        padding: 14px 14px;
                        border-bottom: 1px solid #DCE4EE;
                        text-align: left;
                        white-space: nowrap;
                    }

                    .entrance-admin-table td {
                        padding: 15px 14px;
                        border-bottom: 1px solid #E8EDF4;
                        vertical-align: middle;
                        color: #172033;
                        font-size: 13px;
                    }

                    .entrance-admin-table tbody tr {
                        transition: background .18s ease;
                    }

                    .entrance-admin-table tbody tr:hover {
                        background: #FBFCFE;
                    }

                    .entrance-admin-input {
                        width: 100%;
                        height: 44px;
                        border: 1px solid #DCE4EE;
                        border-radius: 10px;
                        background: #FFFFFF;
                        color: #172033;
                        padding: 0 14px 0 42px;
                        outline: none;
                        transition: border-color .2s ease, box-shadow .2s ease;
                        box-sizing: border-box;
                    }

                    .entrance-admin-input:focus {
                        border-color: #063A6B;
                        box-shadow: 0 0 0 3px rgba(6, 58, 107, .09);
                    }

                    .entrance-admin-select {
                        width: 100%;
                        height: 44px;
                        border: 1px solid #DCE4EE;
                        border-radius: 10px;
                        background: #FFFFFF;
                        color: #172033;
                        padding: 0 12px;
                        outline: none;
                        box-sizing: border-box;
                    }

                    .entrance-admin-button {
                        border: none;
                        cursor: pointer;
                        transition: transform .18s ease, box-shadow .18s ease, opacity .18s ease;
                    }

                    .entrance-admin-button:hover:not(:disabled) {
                        transform: translateY(-1px);
                    }

                    .entrance-admin-button:disabled {
                        cursor: not-allowed;
                        opacity: .55;
                    }

                    @media (max-width: 768px) {
                        .entrance-admin-page {
                            padding: 14px !important;
                        }

                        .entrance-admin-header {
                            flex-direction: column !important;
                            align-items: flex-start !important;
                        }

                        .entrance-admin-filter-grid {
                            grid-template-columns: 1fr !important;
                        }

                        .entrance-admin-summary {
                            grid-template-columns: 1fr !important;
                        }

                        .entrance-admin-modal {
                            width: calc(100vw - 24px) !important;
                            max-height: calc(100vh - 24px) !important;
                        }
                    }
                `}
            </style>

            <div className="entrance-admin-page">
                <div
                    className="entrance-admin-header"
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 18,
                        marginBottom: 22,
                    }}
                >
                    <div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                marginBottom: 7,
                            }}
                        >
                            <div
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 12,
                                    background: COLORS.navy,
                                    color: COLORS.gold,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow:
                                        "0 8px 20px rgba(6,42,75,.15)",
                                }}
                            >
                                <FaShieldAlt size={19} />
                            </div>

                            <div>
                                <h1
                                    style={{
                                        margin: 0,
                                        fontSize: 24,
                                        fontWeight: 850,
                                        color: COLORS.navy,
                                    }}
                                >
                                    Entrance Payment Verification
                                </h1>

                                <p
                                    style={{
                                        margin: "4px 0 0",
                                        color: COLORS.muted,
                                        fontSize: 13,
                                    }}
                                >
                                    Review submitted entrance payments and
                                    verify eligible applicants.
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="entrance-admin-button"
                        onClick={handleRefresh}
                        disabled={loading}
                        style={{
                            height: 42,
                            padding: "0 16px",
                            borderRadius: 10,
                            background: COLORS.navy,
                            color: COLORS.white,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 8,
                            fontWeight: 750,
                        }}
                    >
                        {loading ? (
                            <FaSpinner
                                size={14}
                                style={{
                                    animation:
                                        "spin 1s linear infinite",
                                }}
                            />
                        ) : (
                            <FaShieldAlt size={14} />
                        )}
                        Refresh
                    </button>
                </div>

                <div
                    className="entrance-admin-summary"
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(3, minmax(0, 1fr))",
                        gap: 14,
                        marginBottom: 18,
                    }}
                >
                    <div
                        style={{
                            background: COLORS.white,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 14,
                            padding: 17,
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                        }}
                    >
                        <div
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 11,
                                background: "#EAF1F8",
                                color: COLORS.blue,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FaUsers size={18} />
                        </div>

                        <div>
                            <div
                                style={{
                                    color: COLORS.muted,
                                    fontSize: 12,
                                    fontWeight: 700,
                                }}
                            >
                                Total Payments
                            </div>

                            <div
                                style={{
                                    color: COLORS.navy,
                                    fontSize: 22,
                                    fontWeight: 850,
                                }}
                            >
                                {count}
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            background: COLORS.white,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 14,
                            padding: 17,
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                        }}
                    >
                        <div
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 11,
                                background: "#FFF6DD",
                                color: COLORS.orange,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FaFileInvoiceDollar size={18} />
                        </div>

                        <div>
                            <div
                                style={{
                                    color: COLORS.muted,
                                    fontSize: 12,
                                    fontWeight: 700,
                                }}
                            >
                                Current Page
                            </div>

                            <div
                                style={{
                                    color: COLORS.navy,
                                    fontSize: 22,
                                    fontWeight: 850,
                                }}
                            >
                                {page} / {totalPages}
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            background: COLORS.white,
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 14,
                            padding: 17,
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                        }}
                    >
                        <div
                            style={{
                                width: 42,
                                height: 42,
                                borderRadius: 11,
                                background: "#EAF7EC",
                                color: COLORS.green,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FaUserCheck size={18} />
                        </div>

                        <div>
                            <div
                                style={{
                                    color: COLORS.muted,
                                    fontSize: 12,
                                    fontWeight: 700,
                                }}
                            >
                                Displaying
                            </div>

                            <div
                                style={{
                                    color: COLORS.navy,
                                    fontSize: 16,
                                    fontWeight: 850,
                                }}
                            >
                                {pageStart}–{pageEnd}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    style={{
                        background: COLORS.white,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 15,
                        padding: 16,
                        marginBottom: 18,
                    }}
                >
                    <div
                        className="entrance-admin-filter-grid"
                        style={{
                            display: "grid",
                            gridTemplateColumns:
                                "minmax(260px, 1fr) 190px auto",
                            gap: 12,
                            alignItems: "center",
                        }}
                    >
                        <div style={{ position: "relative" }}>
                            <FaSearch
                                size={14}
                                style={{
                                    position: "absolute",
                                    left: 15,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color: COLORS.muted,
                                    pointerEvents: "none",
                                }}
                            />

                            <input
                                type="text"
                                value={searchInput}
                                onChange={handleSearchChange}
                                placeholder="Search applicant, email, application number..."
                                className="entrance-admin-input"
                            />
                        </div>

                        <div
                            style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                            }}
                        >
                            <FaFilter
                                size={13}
                                style={{
                                    position: "absolute",
                                    left: 12,
                                    color: COLORS.muted,
                                    pointerEvents: "none",
                                }}
                            />

                            <select
                                value={statusFilter}
                                onChange={handleStatusChange}
                                className="entrance-admin-select"
                                style={{
                                    paddingLeft: 34,
                                }}
                            >
                                <option value="">
                                    All Payment Statuses
                                </option>
                                <option value="pending">
                                    Pending
                                </option>
                                <option value="submitted">
                                    Submitted
                                </option>
                                <option value="verified">
                                    Verified
                                </option>
                                <option value="rejected">
                                    Rejected
                                </option>
                                <option value="failed">
                                    Failed
                                </option>
                            </select>
                        </div>

                        <button
                            type="button"
                            className="entrance-admin-button"
                            onClick={clearFilters}
                            style={{
                                height: 44,
                                padding: "0 17px",
                                borderRadius: 10,
                                border: `1px solid ${COLORS.border}`,
                                background: COLORS.light,
                                color: COLORS.navy,
                                fontWeight: 750,
                            }}
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>

                <div
                    style={{
                        background: COLORS.white,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: 15,
                        overflow: "hidden",
                        boxShadow:
                            "0 8px 30px rgba(6,42,75,.04)",
                    }}
                >
                    <div
                        style={{
                            padding: "17px 18px",
                            borderBottom: `1px solid ${COLORS.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 10,
                        }}
                    >
                        <div>
                            <h2
                                style={{
                                    margin: 0,
                                    fontSize: 16,
                                    color: COLORS.navy,
                                    fontWeight: 850,
                                }}
                            >
                                Submitted Entrance Payments
                            </h2>

                            <p
                                style={{
                                    margin: "4px 0 0",
                                    color: COLORS.muted,
                                    fontSize: 12,
                                }}
                            >
                                Verify payments before granting entrance
                                examination access.
                            </p>
                        </div>

                        <FaMoneyBillWave
                            size={19}
                            color={COLORS.gold}
                        />
                    </div>

                    <div className="entrance-admin-scroll">
                        <table className="entrance-admin-table">
                            <thead>
                                <tr>
                                    <th>Applicant</th>
                                    <th>Application</th>
                                    <th>Program</th>
                                    <th>Payment</th>
                                    <th>Amount</th>
                                    <th>Reference</th>
                                    <th>Proof</th>
                                    <th>Status</th>
                                    <th>Submitted</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td
                                            colSpan="10"
                                            style={{
                                                padding: "55px 20px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <FaSpinner
                                                size={26}
                                                color={COLORS.navy}
                                                style={{
                                                    animation:
                                                        "spin 1s linear infinite",
                                                    marginBottom: 10,
                                                }}
                                            />

                                            <div
                                                style={{
                                                    color: COLORS.muted,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                Loading entrance payments...
                                            </div>
                                        </td>
                                    </tr>
                                ) : payments.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan="10"
                                            style={{
                                                padding: "60px 20px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    width: 54,
                                                    height: 54,
                                                    borderRadius: "50%",
                                                    margin: "0 auto 12px",
                                                    background:
                                                        COLORS.light,
                                                    color: COLORS.muted,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent:
                                                        "center",
                                                }}
                                            >
                                                <FaFileInvoiceDollar
                                                    size={22}
                                                />
                                            </div>

                                            <div
                                                style={{
                                                    color: COLORS.navy,
                                                    fontWeight: 800,
                                                    marginBottom: 5,
                                                }}
                                            >
                                                No Payments Found
                                            </div>

                                            <div
                                                style={{
                                                    color: COLORS.muted,
                                                    fontSize: 13,
                                                }}
                                            >
                                                No entrance payment records
                                                match the current search or
                                                filter.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    payments.map((payment) => {
                                        const paymentKey =
                                            payment.id ||
                                            payment.application_number;

                                        const normalizedStatus =
                                            String(
                                                payment.status || ""
                                            )
                                                .trim()
                                                .toLowerCase();

                                        const canVerify =
                                            payment.can_verify !==
                                                false &&
                                            (normalizedStatus ===
                                                "pending" ||
                                                normalizedStatus ===
                                                    "submitted");

                                        const isVerifying =
                                            verifyingId === paymentKey;

                                        return (
                                            <tr key={paymentKey}>
                                                <td>
                                                    <div
                                                        style={{
                                                            display:
                                                                "flex",
                                                            alignItems:
                                                                "center",
                                                            gap: 10,
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                width: 34,
                                                                height: 34,
                                                                minWidth: 34,
                                                                borderRadius:
                                                                    10,
                                                                background:
                                                                    "#EAF1F8",
                                                                color:
                                                                    COLORS
                                                                        .navy,
                                                                display:
                                                                    "flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                fontWeight:
                                                                    850,
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            {String(
                                                                payment.applicant_name ||
                                                                    "A"
                                                            )
                                                                .charAt(0)
                                                                .toUpperCase()}
                                                        </div>

                                                        <div>
                                                            <div
                                                                style={{
                                                                    fontWeight:
                                                                        800,
                                                                    color:
                                                                        COLORS.navy,
                                                                    whiteSpace:
                                                                        "nowrap",
                                                                }}
                                                            >
                                                                {payment.applicant_name ||
                                                                    "—"}
                                                            </div>

                                                            <div
                                                                style={{
                                                                    color:
                                                                        COLORS.muted,
                                                                    fontSize: 11,
                                                                    marginTop: 2,
                                                                }}
                                                            >
                                                                {payment.email ||
                                                                    "No email"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td>
                                                    <span
                                                        style={{
                                                            fontWeight: 800,
                                                            color:
                                                                COLORS.blue,
                                                        }}
                                                    >
                                                        {payment.application_number ||
                                                            "—"}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div
                                                        style={{
                                                            maxWidth: 180,
                                                            fontWeight: 650,
                                                        }}
                                                    >
                                                        {payment.program ||
                                                            "—"}
                                                    </div>
                                                </td>

                                                <td>
                                                    <div
                                                        style={{
                                                            fontWeight: 750,
                                                        }}
                                                    >
                                                        {payment.payment_type_display ||
                                                            payment.payment_type ||
                                                            "—"}
                                                    </div>

                                                    {payment.mobile_provider_display && (
                                                        <div
                                                            style={{
                                                                color:
                                                                    COLORS.muted,
                                                                fontSize: 11,
                                                                marginTop: 2,
                                                            }}
                                                        >
                                                            {
                                                                payment.mobile_provider_display
                                                            }
                                                        </div>
                                                    )}
                                                </td>

                                                <td>
                                                    <span
                                                        style={{
                                                            fontWeight: 850,
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {formatAmount(
                                                            payment.amount,
                                                            payment.currency
                                                        )}
                                                    </span>
                                                </td>

                                                <td>
                                                    <span
                                                        style={{
                                                            fontFamily:
                                                                "monospace",
                                                            fontSize: 11,
                                                            color:
                                                                COLORS.muted,
                                                        }}
                                                    >
                                                        {payment.transaction_reference ||
                                                            "—"}
                                                    </span>
                                                </td>

                                                <td>
                                                    {payment.payment_proof ? (
                                                        <a
                                                            href={
                                                                payment.payment_proof
                                                            }
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                display:
                                                                    "inline-flex",
                                                                alignItems:
                                                                    "center",
                                                                gap: 5,
                                                                padding:
                                                                    "6px 9px",
                                                                borderRadius:
                                                                    8,
                                                                background:
                                                                    "#EEF5FB",
                                                                color:
                                                                    COLORS.blue,
                                                                textDecoration:
                                                                    "none",
                                                                fontWeight:
                                                                    750,
                                                                fontSize: 11,
                                                            }}
                                                        >
                                                            <FaEye
                                                                size={11}
                                                            />
                                                            View
                                                        </a>
                                                    ) : (
                                                        <span
                                                            style={{
                                                                color:
                                                                    COLORS.muted,
                                                                fontSize: 12,
                                                            }}
                                                        >
                                                            None
                                                        </span>
                                                    )}
                                                </td>

                                                <td>
                                                    <StatusBadge
                                                        status={
                                                            payment.status
                                                        }
                                                    />
                                                </td>

                                                <td>
                                                    <span
                                                        style={{
                                                            whiteSpace:
                                                                "nowrap",
                                                            color:
                                                                COLORS.muted,
                                                            fontSize: 11,
                                                        }}
                                                    >
                                                        {formatDateTime(
                                                            payment.submitted_at
                                                        )}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div
                                                        style={{
                                                            display:
                                                                "flex",
                                                            gap: 7,
                                                        }}
                                                    >
                                                        <button
                                                            type="button"
                                                            className="entrance-admin-button"
                                                            onClick={() =>
                                                                openPaymentDetails(
                                                                    payment
                                                                )
                                                            }
                                                            style={{
                                                                width: 34,
                                                                height: 34,
                                                                borderRadius:
                                                                    9,
                                                                background:
                                                                    "#EEF5FB",
                                                                color:
                                                                    COLORS.blue,
                                                                display:
                                                                    "inline-flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                            }}
                                                            title="View payment"
                                                        >
                                                            <FaEye
                                                                size={13}
                                                            />
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="entrance-admin-button"
                                                            onClick={() =>
                                                                verifyPayment(
                                                                    payment
                                                                )
                                                            }
                                                            disabled={
                                                                !canVerify ||
                                                                isVerifying
                                                            }
                                                            style={{
                                                                height: 34,
                                                                padding:
                                                                    "0 10px",
                                                                borderRadius:
                                                                    9,
                                                                background:
                                                                    canVerify
                                                                        ? COLORS.green
                                                                        : "#EEF1F4",
                                                                color:
                                                                    canVerify
                                                                        ? COLORS.white
                                                                        : COLORS.muted,
                                                                display:
                                                                    "inline-flex",
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "center",
                                                                gap: 6,
                                                                fontSize: 11,
                                                                fontWeight:
                                                                    800,
                                                            }}
                                                        >
                                                            {isVerifying ? (
                                                                <FaSpinner
                                                                    size={12}
                                                                    style={{
                                                                        animation:
                                                                            "spin 1s linear infinite",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <FaCheckCircle
                                                                    size={12}
                                                                />
                                                            )}

                                                            {isVerifying
                                                                ? "Verifying..."
                                                                : "Verify"}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    <div
                        style={{
                            padding: "14px 17px",
                            borderTop: `1px solid ${COLORS.border}`,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                        }}
                    >
                        <div
                            style={{
                                color: COLORS.muted,
                                fontSize: 12,
                                fontWeight: 650,
                            }}
                        >
                            Showing {pageStart}–{pageEnd} of {count} payment
                            {count === 1 ? "" : "s"}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                            }}
                        >
                            <button
                                type="button"
                                className="entrance-admin-button"
                                onClick={() =>
                                    loadPayments(Math.max(1, page - 1))
                                }
                                disabled={
                                    loading ||
                                    page <= 1 ||
                                    !previousUrl
                                }
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 9,
                                    background:
                                        page > 1 && previousUrl
                                            ? COLORS.navy
                                            : "#EEF1F4",
                                    color:
                                        page > 1 && previousUrl
                                            ? COLORS.white
                                            : COLORS.muted,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                title="Previous page"
                            >
                                <FaArrowLeft size={13} />
                            </button>

                            <div
                                style={{
                                    minWidth: 80,
                                    textAlign: "center",
                                    color: COLORS.navy,
                                    fontSize: 13,
                                    fontWeight: 850,
                                }}
                            >
                                Page {page} of {totalPages}
                            </div>

                            <button
                                type="button"
                                className="entrance-admin-button"
                                onClick={() =>
                                    loadPayments(
                                        Math.min(
                                            totalPages,
                                            page + 1
                                        )
                                    )
                                }
                                disabled={
                                    loading ||
                                    page >= totalPages ||
                                    !nextUrl
                                }
                                style={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: 9,
                                    background:
                                        page < totalPages &&
                                        nextUrl
                                            ? COLORS.navy
                                            : "#EEF1F4",
                                    color:
                                        page < totalPages &&
                                        nextUrl
                                            ? COLORS.white
                                            : COLORS.muted,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                title="Next page"
                            >
                                <FaArrowRight size={13} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {selectedPayment && (
                <div
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            closePaymentDetails();
                        }
                    }}
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1000,
                        background:
                            "rgba(6, 42, 75, 0.48)",
                        backdropFilter: "blur(4px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 12,
                    }}
                >
                    <div
                        className="entrance-admin-modal"
                        style={{
                            width: "min(760px, calc(100vw - 30px))",
                            maxHeight: "calc(100vh - 30px)",
                            overflowY: "auto",
                            background: COLORS.white,
                            borderRadius: 18,
                            boxShadow:
                                "0 30px 90px rgba(0,0,0,.25)",
                            border: `1px solid ${COLORS.border}`,
                        }}
                    >
                        <div
                            style={{
                                padding: "18px 20px",
                                borderBottom: `1px solid ${COLORS.border}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 15,
                            }}
                        >
                            <div>
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 9,
                                    }}
                                >
                                    <FaFileInvoiceDollar
                                        color={COLORS.gold}
                                    />

                                    <h3
                                        style={{
                                            margin: 0,
                                            color: COLORS.navy,
                                            fontSize: 18,
                                            fontWeight: 850,
                                        }}
                                    >
                                        Payment Details
                                    </h3>
                                </div>

                                <div
                                    style={{
                                        color: COLORS.muted,
                                        fontSize: 12,
                                        marginTop: 4,
                                    }}
                                >
                                    {
                                        selectedPayment.application_number
                                    }
                                </div>
                            </div>

                            <button
                                type="button"
                                className="entrance-admin-button"
                                onClick={closePaymentDetails}
                                disabled={!!verifyingId}
                                style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 9,
                                    background: "#F3F5F8",
                                    color: COLORS.muted,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <FaTimesCircle size={16} />
                            </button>
                        </div>

                        <div
                            style={{
                                padding: 20,
                            }}
                        >
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns:
                                        "repeat(2, minmax(0, 1fr))",
                                    gap: 12,
                                }}
                            >
                                {[
                                    [
                                        "Applicant",
                                        selectedPayment.applicant_name,
                                    ],
                                    [
                                        "Email",
                                        selectedPayment.email,
                                    ],
                                    [
                                        "Phone",
                                        selectedPayment.phone,
                                    ],
                                    [
                                        "Program",
                                        selectedPayment.program,
                                    ],
                                    [
                                        "Academic Year",
                                        selectedPayment
                                            .academic_year?.name ||
                                            selectedPayment
                                                .academic_year_name,
                                    ],
                                    [
                                        "Payment Type",
                                        selectedPayment.payment_type_display ||
                                            selectedPayment.payment_type,
                                    ],
                                    [
                                        "Mobile Provider",
                                        selectedPayment.mobile_provider_display ||
                                            selectedPayment.mobile_provider,
                                    ],
                                    [
                                        "Bank",
                                        selectedPayment.bank_display ||
                                            selectedPayment.bank,
                                    ],
                                    [
                                        "Amount",
                                        formatAmount(
                                            selectedPayment.amount,
                                            selectedPayment.currency
                                        ),
                                    ],
                                    [
                                        "Transaction Reference",
                                        selectedPayment.transaction_reference,
                                    ],
                                    [
                                        "Submitted",
                                        formatDateTime(
                                            selectedPayment.submitted_at
                                        ),
                                    ],
                                    [
                                        "Updated",
                                        formatDateTime(
                                            selectedPayment.updated_at
                                        ),
                                    ],
                                    [
                                        "Verified At",
                                        formatDateTime(
                                            selectedPayment.verified_at
                                        ),
                                    ],
                                    [
                                        "Status",
                                        <StatusBadge
                                            status={
                                                selectedPayment.status
                                            }
                                        />,
                                    ],
                                ].map(([label, value]) => (
                                    <div
                                        key={label}
                                        style={{
                                            padding: 13,
                                            border: `1px solid ${COLORS.border}`,
                                            borderRadius: 11,
                                            background:
                                                "#FBFCFE",
                                        }}
                                    >
                                        <div
                                            style={{
                                                color: COLORS.muted,
                                                fontSize: 10,
                                                fontWeight: 800,
                                                textTransform:
                                                    "uppercase",
                                                letterSpacing:
                                                    ".04em",
                                                marginBottom: 5,
                                            }}
                                        >
                                            {label}
                                        </div>

                                        <div
                                            style={{
                                                color: COLORS.text,
                                                fontSize: 13,
                                                fontWeight: 700,
                                                wordBreak:
                                                    "break-word",
                                            }}
                                        >
                                            {value || "—"}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {selectedPayment.notes && (
                                <div
                                    style={{
                                        marginTop: 14,
                                        padding: 14,
                                        borderRadius: 11,
                                        background: "#FFF9E9",
                                        border: "1px solid #F3E1A4",
                                    }}
                                >
                                    <div
                                        style={{
                                            color: COLORS.orange,
                                            fontSize: 11,
                                            fontWeight: 850,
                                            textTransform:
                                                "uppercase",
                                            marginBottom: 5,
                                        }}
                                    >
                                        Verification Notes
                                    </div>

                                    <div
                                        style={{
                                            color: COLORS.text,
                                            fontSize: 13,
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {selectedPayment.notes}
                                    </div>
                                </div>
                            )}

                            {selectedPayment.payment_proof && (
                                <div
                                    style={{
                                        marginTop: 16,
                                        display: "flex",
                                        justifyContent:
                                            "space-between",
                                        alignItems: "center",
                                        gap: 12,
                                        padding: 14,
                                        borderRadius: 11,
                                        background: "#F5F8FC",
                                        border: `1px solid ${COLORS.border}`,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 10,
                                        }}
                                    >
                                        <FaFileInvoiceDollar
                                            color={COLORS.blue}
                                        />

                                        <div>
                                            <div
                                                style={{
                                                    fontWeight: 800,
                                                    color:
                                                        COLORS.navy,
                                                }}
                                            >
                                                Payment Proof
                                            </div>

                                            <div
                                                style={{
                                                    color:
                                                        COLORS.muted,
                                                    fontSize: 11,
                                                }}
                                            >
                                                Open the submitted
                                                payment document.
                                            </div>
                                        </div>
                                    </div>

                                    <a
                                        href={
                                            selectedPayment.payment_proof
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            padding:
                                                "9px 13px",
                                            borderRadius: 9,
                                            background:
                                                COLORS.navy,
                                            color:
                                                COLORS.white,
                                            textDecoration:
                                                "none",
                                            fontSize: 12,
                                            fontWeight: 800,
                                        }}
                                    >
                                        View Proof
                                    </a>
                                </div>
                            )}
                        </div>

                        <div
                            style={{
                                padding: "15px 20px",
                                borderTop: `1px solid ${COLORS.border}`,
                                display: "flex",
                                justifyContent:
                                    "flex-end",
                                gap: 9,
                            }}
                        >
                            <button
                                type="button"
                                className="entrance-admin-button"
                                onClick={closePaymentDetails}
                                disabled={!!verifyingId}
                                style={{
                                    height: 40,
                                    padding: "0 15px",
                                    borderRadius: 9,
                                    background:
                                        COLORS.light,
                                    color: COLORS.navy,
                                    border: `1px solid ${COLORS.border}`,
                                    fontWeight: 750,
                                }}
                            >
                                Close
                            </button>

                            {(() => {
                                const normalizedStatus =
                                    String(
                                        selectedPayment.status ||
                                            ""
                                    )
                                        .trim()
                                        .toLowerCase();

                                const canVerify =
                                    selectedPayment.can_verify !==
                                        false &&
                                    (normalizedStatus ===
                                        "pending" ||
                                        normalizedStatus ===
                                            "submitted");

                                const paymentKey =
                                    selectedPayment.id ||
                                    selectedPayment.application_number;

                                return (
                                    <button
                                        type="button"
                                        className="entrance-admin-button"
                                        onClick={() =>
                                            verifyPayment(
                                                selectedPayment
                                            )
                                        }
                                        disabled={
                                            !canVerify ||
                                            verifyingId ===
                                                paymentKey
                                        }
                                        style={{
                                            height: 40,
                                            padding: "0 17px",
                                            borderRadius: 9,
                                            background:
                                                canVerify
                                                    ? COLORS.green
                                                    : "#EEF1F4",
                                            color:
                                                canVerify
                                                    ? COLORS.white
                                                    : COLORS.muted,
                                            fontWeight: 800,
                                            display:
                                                "inline-flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                            gap: 7,
                                        }}
                                    >
                                        {verifyingId ===
                                        paymentKey ? (
                                            <FaSpinner
                                                size={13}
                                                style={{
                                                    animation:
                                                        "spin 1s linear infinite",
                                                }}
                                            />
                                        ) : (
                                            <FaCheckCircle
                                                size={13}
                                            />
                                        )}

                                        {verifyingId ===
                                        paymentKey
                                            ? "Verifying..."
                                            : "Verify Payment"}
                                    </button>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}

            <style>
                {`
                    @keyframes spin {
                        from {
                            transform: rotate(0deg);
                        }
                        to {
                            transform: rotate(360deg);
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default AdminVerifyEntrance;